import type { PermissionKey } from './permissions.js';

// ---------------------------------------------------------------- identity

export interface Principal {
  /** Entra object ID (`oid`). Immutable; the join key for everything. */
  readonly oid: string;
  /** User principal name. Mutable in Entra, so display only -- never a key. */
  readonly upn: string;
  readonly displayName: string;
  readonly tenantId: string;
  /** Internal user row id. */
  readonly userId: string;
  /**
   * The session this request belongs to, or `null` when it cannot be
   * determined -- which happens when the access token carries no identifier
   * to match a session row against.
   *
   * Nullable on purpose. It was a plain `string`, so the only way to satisfy
   * the type when no session was found was to invent a value, and the
   * invented value reached a `uuid` column and failed every audit write.
   * A type that cannot say "unknown" gets lied to.
   */
  readonly sessionId: string | null;
}

export interface Entitlements {
  readonly roles: readonly EntitlementRole[];
  /** Permissions held without target scoping. Only non-privileged keys appear here. */
  readonly global: ReadonlySet<PermissionKey>;
  /** targetId -> permissions effective on that target. */
  readonly byTarget: ReadonlyMap<string, ReadonlySet<PermissionKey>>;
  readonly resolvedAt: Date;
}

export interface EntitlementRole {
  readonly roleId: string;
  readonly name: string;
  readonly source: 'direct' | 'entra-group' | 'breakglass';
  readonly grantedAt: Date;
  readonly expiresAt: Date | null;
}

// ------------------------------------------------------------- connections

/**
 * `mssql_server` is SQL Server reached as a *server* rather than as one
 * database: its config carries no database name, each submission names the
 * database it runs against, and the object explorer can enumerate what is on
 * the instance. Everything else about it -- the gate, the credentials, the
 * approval policy, the audit event -- is identical to `mssql`.
 */
export const TARGET_KINDS = ['mssql', 'oracle', 'rest', 'mssql_server'] as const;
export type TargetKind = (typeof TARGET_KINDS)[number];

/** Kinds that accept SQL. Written as an allowlist so a new kind is excluded
 * until someone decides otherwise, rather than falling into the SQL path by
 * not being `rest`. */
export const SQL_TARGET_KINDS = ['mssql', 'oracle', 'mssql_server'] as const;
export type SqlTargetKind = (typeof SQL_TARGET_KINDS)[number];

export function isSqlTargetKind(kind: TargetKind): kind is SqlTargetKind {
  return (SQL_TARGET_KINDS as readonly TargetKind[]).includes(kind);
}

/**
 * Does this kind leave the database to be chosen per submission?
 *
 * The one question the rest of the system asks about server scoping. Keeping
 * it a function rather than a `kind === 'mssql_server'` comparison scattered
 * through the code means a second server-scoped kind -- Oracle reached as an
 * instance, say -- is one line here.
 */
export function isServerScopedKind(kind: TargetKind): boolean {
  return kind === 'mssql_server';
}

/**
 * The SQL dialects this tool parses.
 *
 * Fewer than there are target kinds, because `mssql` and `mssql_server` are
 * two ways of addressing one product and share every lexing and
 * classification rule.
 *
 * Here rather than in the connectors package, where it used to live, because
 * three independent things need it: the lexer and the gate, the saved-script
 * library's dialect filter, and the editor's syntax highlighting. Two of
 * those run in the browser, which cannot import a package that loads the SQL
 * drivers.
 */
export const SQL_DIALECTS = ['mssql', 'oracle'] as const;
export type SqlDialect = (typeof SQL_DIALECTS)[number];

/**
 * What a target parses as, or `null` when it parses as nothing.
 *
 * `null` for REST rather than a default, which is the whole reason this
 * exists as a function. The browser had its own copy of this decision as
 * `kind === 'oracle' ? 'oracle' : 'mssql'`, which answers "mssql" for a REST
 * target -- harmless while the only caller was a SQL console that never holds
 * one, and not harmless the moment a second caller filters a script library
 * by the same question and gets a different answer.
 *
 * A switch rather than a lookup so that adding a target kind without deciding
 * what it parses as is a compile error.
 */
export function sqlDialectForKind(kind: TargetKind): SqlDialect | null {
  switch (kind) {
    case 'mssql':
    case 'mssql_server':
      return 'mssql';
    case 'oracle':
      return 'oracle';
    case 'rest':
      return null;
  }
}

export const ENVIRONMENTS = ['dev', 'test', 'staging', 'prod'] as const;
export type TargetEnvironment = (typeof ENVIRONMENTS)[number];

export interface DatabaseTargetConfig {
  readonly host: string;
  readonly port: number;
  /** SQL Server database name, or Oracle service name. */
  readonly database: string;
  readonly encrypt: boolean;
  /**
   * Only ever false for a documented legacy target. Oracle thin mode has no
   * native network encryption, so TLS is the only transport protection.
   */
  readonly trustServerCertificate: boolean;
}

/**
 * A SQL Server instance with no database named.
 *
 * Structurally `DatabaseTargetConfig` minus `database`, and deliberately not
 * modelled as that type with the field made optional: an optional `database`
 * would type-check everywhere the required one does, and every existing
 * `config.database` read would start silently returning `undefined` against a
 * server target instead of failing to compile.
 */
export interface ServerTargetConfig {
  readonly host: string;
  readonly port: number;
  readonly encrypt: boolean;
  readonly trustServerCertificate: boolean;
}

/**
 * System databases, never offered and never accepted.
 *
 * Not a security boundary -- the read account's permissions are that -- but
 * `tempdb` and `model` hold nothing a support engineer wants, and `master`
 * holds server configuration. Excluding them keeps the explorer's first
 * screen to the sixteen databases somebody might actually be looking for.
 */
export const SYSTEM_DATABASES = ['master', 'model', 'msdb', 'tempdb'] as const;

export function isSystemDatabase(name: string): boolean {
  return (SYSTEM_DATABASES as readonly string[]).includes(name.toLowerCase());
}

export interface RestTargetConfig {
  readonly baseUrl: string;
  /** Paths callable on this target. Prefix match, no wildcards in the host. */
  readonly pathAllowlist: readonly string[];
  readonly allowedMethods: readonly HttpMethod[];
  readonly defaultHeaders: Readonly<Record<string, string>>;
  /**
   * Private CIDRs this target may reach.
   *
   * Empty -- the default -- means public addresses only. An internal API
   * inside the VPC needs its specific range listed here, which is a
   * reviewed change to the target rather than something a request can ask
   * for. Link-local and loopback are never reachable regardless of what
   * appears in this list; see the SSRF guard in @csa/connectors.
   */
  readonly allowedEgressCidrs: readonly string[];
  /** Requests per second permitted by the token bucket for this target. */
  readonly rateLimitPerSecond: number;
  readonly rateLimitBurst: number;
}

export const HTTP_METHODS = ['GET', 'HEAD', 'OPTIONS', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

/** Methods that cannot change state and so never require approval on their own. */
export const SAFE_HTTP_METHODS: readonly HttpMethod[] = ['GET', 'HEAD', 'OPTIONS'];

export function isSafeMethod(m: HttpMethod): boolean {
  return SAFE_HTTP_METHODS.includes(m);
}

export interface ConnectionTarget {
  readonly id: string;
  /** Stable human-readable key used in policies and audit records. */
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly kind: TargetKind;
  readonly environment: TargetEnvironment;
  readonly enabled: boolean;
  readonly config: DatabaseTargetConfig | RestTargetConfig | ServerTargetConfig;

  /**
   * Secrets Manager ARNs. The read account is constrained at the database
   * (db_datareader + DENY on SQL Server, ALTER USER READ ONLY on Oracle); the
   * write account is only reachable with a consumed approval token.
   */
  readonly readSecretArn: string | null;
  readonly writeSecretArn: string | null;

  /** Number of FIFO message-group slots, i.e. concurrent jobs against this target. */
  readonly maxConcurrency: number;
  readonly statementTimeoutMs: number;
  readonly interactiveRowCap: number;
  readonly batchRowCap: number;

  /**
   * Permits several statements in one submission, all of which must read.
   *
   * Off unless an administrator turns it on for this target. A script never
   * carries a write, DDL or procedure call whatever this says -- those stay
   * one statement per job, so that each is separately approved and audited.
   */
  readonly allowScripts: boolean;

  /**
   * Stored procedures callable without approval. Empty means none.
   *
   * Two-part `schema.procedure` on a target that names one database;
   * three-part `database.schema.procedure` on a server-scoped target, where
   * the same two-part name means a different procedure in each database.
   */
  readonly procAllowlist: readonly string[];
  /** Schemas visible to introspection and queryable. Empty means all. */
  readonly schemaAllowlist: readonly string[];
  /**
   * Databases a server-scoped target may be asked about. Empty means every
   * online database the service account can open, minus the system ones.
   * Meaningless, and always empty, on a target that names one database.
   */
  readonly databaseAllowlist: readonly string[];

  readonly createdAt: Date;
  readonly updatedAt: Date;
}

// ------------------------------------------------------------- submissions

export const STATEMENT_CLASSES = [
  'read',
  'write',
  'ddl',
  'procedure',
  'transaction-control',
  'unknown',
] as const;
export type StatementClass = (typeof STATEMENT_CLASSES)[number];

export interface SqlSubmission {
  readonly kind: 'sql';
  readonly targetId: string;
  /**
   * Which database on a server-scoped target. Null for a target that names
   * one database, where the answer is already in the target's config.
   *
   * A field rather than a `USE` statement because `USE` classifies as
   * transaction-control and is refused by the gate -- correctly, since a
   * statement that can change the current database makes every following
   * statement in a script mean something the approver did not read. As a
   * field it is validated against the allowlist, recorded on the audit event,
   * and covered by the approval payload hash.
   */
  readonly database: string | null;
  readonly statement: string;
  readonly parameters: Readonly<Record<string, unknown>>;
  /** Interactive returns inline and is row-capped; batch streams to S3. */
  readonly mode: ExecutionMode;
  readonly outputFormat: OutputFormat;
  readonly justification: string;
}

export interface ApiSubmission {
  readonly kind: 'api';
  readonly targetId: string;
  readonly method: HttpMethod;
  readonly path: string;
  readonly query: Readonly<Record<string, string>>;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: string | null;
  /** Secret references resolved server-side; values never reach the browser. */
  readonly secretRefs: readonly SecretReference[];
  readonly mode: ExecutionMode;
  readonly justification: string;
}

export type Submission = SqlSubmission | ApiSubmission;

export const EXECUTION_MODES = ['interactive', 'batch'] as const;
export type ExecutionMode = (typeof EXECUTION_MODES)[number];

/**
 * How many times SQS delivers a message before moving it to the dead-letter
 * queue.
 *
 * Declared here because two places need the same number and they are in
 * different packages: the queue stack configures the redrive policy, and the
 * retry policy must give up *before* it fires.
 *
 * That ordering is the whole point. SQS redrive is a backstop for a worker
 * that died before it could decide anything -- it is not the retry policy.
 * When it fires first, the application never records a terminal state: the
 * job stays `queued` with the error from its last attempt, no `dead_letter`
 * row is written because only the application's own exhaustion path writes
 * one, and nothing re-dispatches it because recovery only looks at
 * `approved` jobs. The user is left watching a job that says "Queued"
 * forever. These two numbers drifting apart by one is all it takes.
 */
export const SQS_MAX_RECEIVE_COUNT: Readonly<Record<ExecutionMode, number>> = {
  /**
   * An interactive query that has failed twice will almost certainly fail a
   * third time, and the person who submitted it has already given up.
   */
  interactive: 3,
  /**
   * Two, not three. A batch export that failed once is retried in case it
   * was a Spot reclamation; a second failure is a real fault, and a third
   * attempt at a multi-gigabyte export is an hour of database load spent
   * confirming what is already known.
   */
  batch: 2,
};

export const OUTPUT_FORMATS = ['json', 'csv', 'ndjson', 'parquet'] as const;
export type OutputFormat = (typeof OUTPUT_FORMATS)[number];

/**
 * A pointer to a managed secret plus where to inject it. The browser only ever
 * sees the reference; the worker resolves the value from Secrets Manager and
 * the resolved value is redacted from every log and audit record.
 */
export interface SecretReference {
  /** Secret binding id on the target, not the ARN -- ARNs are not user data. */
  readonly bindingId: string;
  /** JSON pointer into the secret payload, e.g. `/apiKey`. */
  readonly field: string;
  readonly injectAs: 'header' | 'query' | 'bearer' | 'basic-password';
  /** Header or query parameter name. Ignored for bearer. */
  readonly name: string;
}

// -------------------------------------------------------------------- jobs

export const JOB_STATUSES = [
  'pending-approval',
  'approved',
  'queued',
  'running',
  'succeeded',
  'failed',
  'cancelled',
  'rejected',
  'expired',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = [
  'succeeded',
  'failed',
  'cancelled',
  'rejected',
  'expired',
];

export function isTerminal(status: JobStatus): boolean {
  return TERMINAL_JOB_STATUSES.includes(status);
}

export interface Job {
  readonly id: string;
  readonly kind: 'sql' | 'api';
  readonly targetId: string;
  readonly submittedByUserId: string;
  readonly submittedByUpn: string;
  readonly status: JobStatus;
  readonly mode: ExecutionMode;

  /** SHA-256 of the canonical submission. Approval binds to this exact value. */
  readonly payloadHash: string;
  readonly approvalRequestId: string | null;
  /** Client-supplied key that makes resubmission safe. */
  readonly idempotencyKey: string;

  readonly statementClass: StatementClass | null;
  readonly justification: string;

  readonly rowCount: number | null;
  readonly byteCount: number | null;
  readonly resultKey: string | null;
  readonly resultFormat: OutputFormat | null;
  readonly httpStatus: number | null;

  readonly error: JobError | null;
  readonly attempt: number;

  readonly createdAt: Date;
  readonly queuedAt: Date | null;
  readonly startedAt: Date | null;
  readonly finishedAt: Date | null;
  readonly durationMs: number | null;
}

export interface JobError {
  readonly code: string;
  /** Already redacted. Safe to show a user and safe to persist. */
  readonly message: string;
  readonly retryable: boolean;
}

export interface JobProgress {
  readonly jobId: string;
  readonly phase: 'connecting' | 'executing' | 'streaming' | 'uploading' | 'finalising';
  readonly rowsProcessed: number;
  readonly bytesWritten: number;
  /** Null when the driver cannot estimate the total. */
  readonly percent: number | null;
  readonly message: string;
  readonly at: Date;
}

// --------------------------------------------------------------- approvals

export const APPROVAL_STATUSES = [
  'pending',
  'granted',
  'rejected',
  'expired',
  'cancelled',
  'consumed',
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export interface ApprovalRequest {
  readonly id: string;
  readonly jobId: string;
  readonly requestedByUserId: string;
  readonly requestedByUpn: string;
  readonly targetId: string;
  /** The hash the execution path will re-derive and compare against. */
  readonly payloadHash: string;
  /** Human-readable rendering of exactly what was approved. */
  readonly payloadSummary: string;
  readonly requiredApprovals: number;
  readonly status: ApprovalStatus;
  readonly matchedRuleId: string | null;
  readonly justification: string;
  readonly expiresAt: Date;
  readonly createdAt: Date;
  readonly decidedAt: Date | null;
  readonly consumedAt: Date | null;
  readonly decisions: readonly ApprovalDecision[];
}

export interface ApprovalDecision {
  readonly id: string;
  readonly approvalRequestId: string;
  readonly approverUserId: string;
  readonly approverUpn: string;
  readonly decision: 'granted' | 'rejected';
  readonly comment: string;
  readonly decidedAt: Date;
}

export interface ApprovalRule {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly enabled: boolean;
  /** Lower runs first; the first match wins. */
  readonly priority: number;
  readonly match: ApprovalRuleMatch;
  readonly requiredApprovals: number;
  readonly ttlMinutes: number;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

/** All present fields must match. Absent fields are wildcards. */
export interface ApprovalRuleMatch {
  readonly kinds?: readonly ('sql' | 'api')[];
  readonly environments?: readonly TargetEnvironment[];
  readonly targetKeys?: readonly string[];
  readonly statementClasses?: readonly StatementClass[];
  readonly httpMethods?: readonly HttpMethod[];
  /** Triggers when a procedure is not on the target allowlist. */
  readonly procedureNotAllowlisted?: boolean;
  /** Triggers when the estimated or actual row count exceeds this. */
  readonly rowCountAbove?: number;
  readonly usesWriteAccount?: boolean;
}

// ------------------------------------------------------------------ results

export interface QueryResultPage {
  readonly columns: readonly ResultColumn[];
  readonly rows: readonly (readonly unknown[])[];
  readonly rowCount: number;
  /** True when the row cap stopped the read before the result was exhausted. */
  readonly truncated: boolean;
  readonly durationMs: number;
}

export interface ResultColumn {
  readonly name: string;
  readonly dataType: string;
  readonly nullable: boolean;
}

export interface ResultArtifact {
  readonly jobId: string;
  readonly key: string;
  readonly format: OutputFormat;
  readonly byteCount: number;
  readonly rowCount: number;
  readonly checksumSha256: string;
  readonly expiresAt: Date;
}

// ------------------------------------------------------------------ schema

export interface SchemaColumn {
  readonly name: string;
  readonly dataType: string;
  readonly nullable: boolean;
}

/**
 * What the object explorer can be asked for.
 *
 * Tables and views are listed so their rows can be read; procedures and
 * functions are listed so a call to one can be scripted. There is no fifth
 * entry, and in particular no way to ask for an object's definition -- a
 * procedure body is source code, and disclosing it is a different decision
 * from disclosing that the procedure exists.
 */
export const EXPLORER_OBJECT_TYPES = ['table', 'view', 'procedure', 'function'] as const;
export type ExplorerObjectType = (typeof EXPLORER_OBJECT_TYPES)[number];

export interface ExplorerDatabase {
  readonly name: string;
  /**
   * Whether the read account can open it.
   *
   * Reported rather than filtered out. A database the service account cannot
   * read is a fact a support engineer needs -- it is the difference between
   * "there is nothing here" and "I cannot see in here", and those have
   * different owners and different fixes.
   */
  readonly accessible: boolean;
}

export interface ExplorerObject {
  readonly schema: string;
  readonly name: string;
  readonly type: ExplorerObjectType;
}

export interface ExplorerParameter {
  readonly name: string;
  readonly dataType: string;
  /** OUTPUT parameters are scripted as such and not given a value. */
  readonly isOutput: boolean;
  readonly hasDefault: boolean;
}

// --------------------------------------------------------- script library

/**
 * Who can see a saved script.
 *
 * Ordered from narrowest to widest, which is the order the console offers
 * them in and the order a reviewer should read them in.
 */
export const SCRIPT_VISIBILITIES = ['private', 'role', 'organisation'] as const;
export type ScriptVisibility = (typeof SCRIPT_VISIBILITIES)[number];

/**
 * The visibilities that have to be asked for rather than simply set.
 *
 * `private` is absent because narrowing needs no permission: an author
 * retracting advice they now believe is wrong should not wait for anybody.
 */
export const SHAREABLE_VISIBILITIES = ['role', 'organisation'] as const;
export type ShareableVisibility = (typeof SHAREABLE_VISIBILITIES)[number];

export const SCRIPT_PUBLICATION_STATUSES = [
  'pending',
  'granted',
  'rejected',
  'expired',
  'cancelled',
  /** A newer version was submitted while this one was still pending. */
  'superseded',
] as const;
export type ScriptPublicationStatus = (typeof SCRIPT_PUBLICATION_STATUSES)[number];

/**
 * One row of the library, without the statement.
 *
 * The text is deliberately absent. The list would otherwise ship every
 * production statement the caller can see to a browser in order to draw a
 * list of names, which is both wasteful and a disclosure nobody asked for.
 * It is fetched when a script is opened.
 */
export interface ScriptSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly ownerUpn: string;
  /** True when the caller owns it, which is what unlocks editing and history. */
  readonly isOwner: boolean;
  readonly visibility: ScriptVisibility;
  readonly sharedRoleId: string | null;
  readonly sharedRoleName: string | null;
  readonly dialects: readonly SqlDialect[];
  readonly tags: readonly string[];
  /**
   * The version this caller may read.
   *
   * For the owner, their latest. For everybody else, the one that was
   * approved -- which is not the same number, and conflating them is how an
   * unapproved draft reaches the organisation.
   */
  readonly readableVersion: number;
  /** Present only for the owner. Null tells the console to hide the history. */
  readonly currentVersion: number | null;
  readonly publishedVersion: number | null;
  /** Set when the owner has edits that are not yet published. */
  readonly hasUnpublishedChanges: boolean;
  readonly pendingPublication: boolean;
  readonly targetId: string | null;
  readonly databaseName: string | null;
  readonly updatedAt: Date;
}

export interface ScriptVersion {
  readonly version: number;
  readonly statement: string;
  readonly note: string;
  readonly authorUpn: string;
  readonly createdAt: Date;
}

/** A version without its statement, for rendering a history list. */
export type ScriptVersionSummary = Omit<ScriptVersion, 'statement'>;

export interface ScriptPublicationRequest {
  readonly id: string;
  readonly scriptId: string;
  readonly scriptName: string;
  readonly version: number;
  readonly statement: string;
  readonly requestedVisibility: ShareableVisibility;
  readonly requestedRoleId: string | null;
  readonly requestedRoleName: string | null;
  readonly requestedByUpn: string;
  readonly justification: string;
  readonly requiredApprovals: number;
  readonly grantedCount: number;
  readonly status: ScriptPublicationStatus;
  readonly createdAt: Date;
  readonly expiresAt: Date;
  /**
   * Whether this approver may decide on it.
   *
   * False on their own request. Computed server-side and sent rather than
   * derived in the browser, so the button being absent and the route
   * refusing are the same decision.
   */
  readonly canDecide: boolean;
}
